import '../assets/css/app.css';
import '../assets/sass/app.scss';
import '../assets/sass/video-tool.scss';
import '@frsource/frs-hide-scrollbar';
import '@/plugins/perfect-scrollbar/style.css';
import '@/plugins/font-source/fonts';
import 'primevue/resources/themes/aura-light-green/theme.css';

import { VueQueryPlugin } from '@tanstack/vue-query';
import { createHead } from '@unhead/vue/client';
import { createPinia } from 'pinia';
import PrimeVue from 'primevue/config';
import ConfirmationService from 'primevue/confirmationservice';
import DialogService from 'primevue/dialogservice';
import StyleClass from 'primevue/styleclass';
import ToastService from 'primevue/toastservice';
import Tooltip from 'primevue/tooltip';
import { createApp } from 'vue';
import { VueRecaptchaPlugin } from 'vue-recaptcha/head';

import { initialize as initializeMixpanel } from '@/helpers/analytics/mixpanel';
import { handleChunkLoadError, isChunkLoadError } from '@/helpers/chunk-error-handler';
import { queryClient } from '@/helpers/query-client';

import App from './App.vue';
import { i18n } from './i18n';
import V3PtOptions from './plugins/primevue/theme';
import router from './routes';

// Bugsnag.start({
//   apiKey: import.meta.env.VITE_BUGSNAG_API_KEY,
//   plugins: [new BugsnagPluginVue()],
//   releaseStage: import.meta.env.VITE_APP_ENV,
//   appVersion: import.meta.env.VITE_APP_VERSION,
// });

window.addEventListener('unhandledrejection', (event) => {
  const errorMessage = event.reason?.message || String(event.reason);

  if (isChunkLoadError(errorMessage)) {
    event.preventDefault();
    handleChunkLoadError();

    return;
  }

  if (event.reason instanceof Error) {
    return;
  }
});

// Handle back button (bfcache) - verify auth when page is restored from cache
window.addEventListener('pageshow', (event) => {
  // If page is restored from bfcache (back button), verify auth status
  if (event.persisted) {
    console.log('Page restored from bfcache, verifying auth...');
    
    // Check if we have cached user data but need to verify
    const cachedUser = window.__DATA__?.user;
    if (cachedUser) {
      // Make API call to verify auth - if not authenticated, clear the cache
      import('./helpers/axios').then(({ axios }) => {
        axios.get('/account/member-preferences')
          .catch((error: any) => {
            if (error.response?.status === 401) {
              console.log('Auth invalid after bfcache restore, clearing state');
              // Clear the cached user data
              window.__DATA__ = { user: null };
              // Force redirect to login
              window.location.href = '/login';
            }
          });
      });
    }
  }
});

// const bugsnagVue = Bugsnag.getPlugin('vue');
const app = createApp(App);
const head = createHead();
const pinia = createPinia();

app.use(head);
// app.use(bugsnagVue);
app.use(i18n);
app.use(PrimeVue, V3PtOptions);
app.config.globalProperties.$primevue.config.locale = {
  firstDayOfWeek: 0,
  dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  dayNamesMin: ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'],
  monthNames: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  monthNamesShort: [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ],
  today: 'Today',
  clear: 'Clear',
  // Add required property to satisfy TS
  fileSizeTypes: ['Bytes', 'KB', 'MB', 'GB', 'TB'],
};
app.use(pinia);
app.use(ToastService);
app.use(ConfirmationService);
app.use(DialogService);
app.use(router);
app.use(VueQueryPlugin, {
  enableDevtoolsV6Plugin: true,
  queryClient,
});
app.use(VueRecaptchaPlugin, {
  v3SiteKey: import.meta.env.VITE_RECAPTCHA_SITE_KEY,
});
app.directive('tooltip', Tooltip);
app.directive('styleclass', StyleClass);

if (['production', 'test'].includes(import.meta.env.VITE_APP_ENV)) {
  initializeMixpanel();
}

app.mount('#app');
